import { useState } from "preact/hooks";
import { t } from "../i18n/index.ts";
import { ignore } from "../lib/ignore.ts";
import { closed } from "../lib/shift.ts";
import { Button } from "./Button.tsx";
import { Dialog, DialogActions } from "./Dialog.tsx";
import { Field, Form, Input } from "./form.tsx";

/** A page number typed: a whole one from 1 to `last`, or none. */
function pageOf(typed: string, last: number): number | null {
  const n = Number(typed.trim());
  return Number.isInteger(n) && n >= 1 && n <= last ? n : null;
}

/** "Go to page": a page of `last` typed (the one shown, `current`, to start with), gone to with `go`. */
export function PageJump(p: {
  open: boolean;
  current: number;
  last: number;
  go: (n: number) => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={p.open} onClose={p.onClose} title={t("pager.jump")}>
      {p.open ? <JumpForm {...p} /> : null}
    </Dialog>
  );
}

function JumpForm(p: { current: number; last: number; go: (n: number) => void; onClose: () => void }) {
  const [typed, setTyped] = useState(String(p.current));
  const n = pageOf(typed, p.last);
  return (
    <Form
      class="flex flex-col gap-4"
      onSubmit={() => {
        if (n !== null) {
          p.onClose();
          // Gone first (in a native app, its sheet too), then to the page: not both at once.
          if (n !== p.current) {
            closed()
              .then(() => p.go(n))
              .catch(ignore);
          }
        }
      }}
    >
      <Field label={t("pager.page")} hint={t("pager.pageHint", { last: p.last })}>
        <Input
          value={typed}
          onValue={setTyped}
          inputMode="numeric"
          autocomplete="off"
          autofocus={true}
          onFocus={(e) => e.currentTarget.select()}
        />
      </Field>
      <DialogActions>
        <Button answer="cancel" onClick={p.onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} disabled={n === null}>
          {t("pager.go")}
        </Button>
      </DialogActions>
    </Form>
  );
}
